import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthView } from './Auth.types';
import { useAuthHandlers } from './useAuthHandlers';
import { getAuthViewContent } from './Auth.config';
import { decodeJWT } from '../../utils/jwt';
import { useAuth } from '../../hooks/useAuth';

const Auth: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = (searchParams.get('view') as AuthView) || 'sign-in';
  const [currentView, setCurrentView] = useState<AuthView>(initialView);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  
  // Get all handlers from custom hook
  const handlers = useAuthHandlers();

  // Redirect authenticated users away from auth page
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Admin is already authenticated, redirect to dashboard
      console.log('Admin is already authenticated, redirecting to /admins');
      navigate('/admins', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Update view when URL params change and validate access
  useEffect(() => {
    // Don't process auth views if still loading or if user is already authenticated
    if (isLoading || isAuthenticated) {
      return;
    }

    const viewParam = (searchParams.get('view') as AuthView) || 'sign-in';
    const token = searchParams.get('token');
    
    // If there's a token in URL, decode it to get email
    if (token && ( viewParam === 'reset-password' || viewParam === 'first-sign-in')) {
      const payload = decodeJWT(token);
      const email = payload?.email;
      
      if (email) {
        // Store email and token from URL
        handlers.setTempData(email, token);
      }
    }
    
    // Check if user has valid session for the requested view
    if (!handlers.hasValidSession(viewParam)) {
      // Invalid access - redirect to sign-in and clear session
      console.warn(`Attempted to access ${viewParam} without valid session. Redirecting to sign-in.`);
      handlers.handleBackToSignIn();
      return;
    }
    
    setCurrentView(viewParam);
  }, [searchParams, handlers, setSearchParams, isAuthenticated, isLoading]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100vw', 
        height: '100vh', 
        backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <Typography sx={{ color: '#FFFFFF' }}>Loading...</Typography>
      </Box>
    );
  }

  // Don't render auth form if user is authenticated (will be redirected)
  if (isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100vw', 
      height: '100vh', 
      backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
      overflow: 'hidden',
    }}>
      {/* Left side - Form */}
      <Box
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          top: 0,
          left: 0,
          width: { xs: '100%', md: '520px' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          padding: { xs: '32px', md: '80px' },
          backgroundColor: '#FFFFFF',
          zIndex: 1,
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png"
          alt="Eventi"
          sx={{
            width: '120px',
            height: '93.98px',
          }}
        />

        {/* Form Content - This should take remaining space and center its content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          {getAuthViewContent(currentView, handlers)}
        </Box>

        {/* Copyright */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: 1.4,
            letterSpacing: '-0.615%',
            color: '#181818',
            opacity: 0.4,
            textAlign: 'left',
          }}
        >
          © Eventi 2025
        </Typography>
      </Box>

      {/* Right side - Illustration (hidden on mobile, visible on desktop) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          top: 0,
          left: '520px',
          width: 'calc(100vw - 520px)',
          height: '100vh',
          backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
        }}
      >
        {/* Pattern Background Image */}
        <Box
          component="img"
          src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-bg-pattern.png"
          alt=""
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* Large Ellipse */}
        <Box
          component="img"
          src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-illustration.svg"
          alt=""
          sx={{
            position: 'absolute',
            top: '237px',
            left: '160.55px',
            width: '680.19px',
            height: '524.69px',
            opacity: 0.7,
            pointerEvents: 'none',
            maxWidth: '70%',
          }}
        />

        {/* Small Circle 1 */}
        <Box
          component="img"
          src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-illustration.svg"
          alt=""
          sx={{
            position: 'absolute',
            top: '355.39px',
            left: '88.26px',
            width: '55.94px',
            height: '55.92px',
            opacity: 0.7,
            pointerEvents: 'none',
          }}
        />

        {/* Small Circle 2 */}
        <Box
          component="img"
          src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-illustration.svg"
          alt=""
          sx={{
            position: 'absolute',
            top: '782.82px',
            left: '774.24px',
            width: '55.94px',
            height: '55.92px',
            opacity: 0.7,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
};

export default Auth;
